import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DocumentService } from '@app/shared/services';
import { SharedVaultService } from '@app/shared/services/vault/shared-vault.service';
import { Editor } from 'ngx-editor';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { AutoUnsubscribe } from '@app/shared/decorators/auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Component({
  selector: 'create-note-dialog',
  templateUrl: './create-note.component.html',
  styleUrls: ['./create-note.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateNoteComponent implements OnInit, OnDestroy {
  @ViewChild('textarea', { static: true }) textarea: ElementRef;
  note = '';
  selectedSecurity_key;
  maxChars = 500;
  noteTitle = '';
  editor: Editor;
  formUserPublicKey = false;
  noteContentCount = 0;
  subscription = new Subscription();

  toolbar = [
    ['bold', 'italic'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];
  colorPresets = ['red', '#FF0000', 'rgb(255, 0, 0)'];
  securityStateObject;
  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    public dialogRef: MatDialogRef<CreateNoteComponent>,
    private documentService: DocumentService,
    private sharedVaultService: SharedVaultService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.securityStateObject = this.data.securityStateObject;
    this.selectedSecurity_key = this.data.selectedSecurity_key;
    this.editor = new Editor();
    // this.textarea.nativeElement.focus();
    if (this.data.isEditing) {
      let parsedNoteObject = JSON.parse(this.data?.vault_user_form_note_note);
      this.noteTitle = parsedNoteObject.title.trim();
      this.note = parsedNoteObject.noteContent.trim();
    }
    if (!this.data.isEditing && this.data?.title) {
      this.noteTitle = this.data.title.trim();
      this.note = this.data.noteContent.trim();
    }
    this.noteContentCount = this.plainTextLength();
    this.cd.detectChanges();
  }

  public_key() {
    return this.securityStateObject.public_key;
  }

  addNote() {
    if (this.note) {
      const payload = {
        note: JSON.stringify({ title: this.noteTitle.trim(), noteContent: this.note.trim() }),
        user_pk: this.public_key(),
        ...(this.data.is_suggestion_note && { suggestion_note_type: this.noteTitle.trim(), suggestion_note_status: 'resolved' }),
      };

      this.subscription.add(
        this.documentService.createFormEncryption(payload, this.data.id ?? this.data.currentFormId, this.selectedSecurity_key).subscribe((res) => {
          this.toastr.success('Note added');
          this.closeDialog(true, this.data.formItemsSetup ? this.noteTitle.trim() : null);
        })
      );
    }
  }

  editNote() {
    const payload = {
      note: JSON.stringify({ title: this.noteTitle.trim(), noteContent: this.note.trim() }),
      user_pk: this.public_key(),
      id: this.data.vault_user_form_note_id,
    };

    this.subscription.add(
      this.documentService.updateNote(payload, this.selectedSecurity_key).subscribe(() => {
        this.closeDialog(true);
      })
    );
  }

  onContentChange(content: string) {
    const plainText = this.stripHtml(content);
    const plainTextLength = plainText.length;
    if (plainTextLength > this.maxChars) {
      if (content !== plainText) {
        this.note = this.truncateHtmlPreservingFormat(content, this.maxChars);
      } else {
        this.note = plainText.substring(0, this.maxChars);
      }
    } else {
      this.note = content;
    }

    this.noteContentCount = plainTextLength;
  }

  onKeyDown(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'PageUp',
      'PageDown',
      'Tab',
      'Escape',
      'Enter',
    ];

    const isSelectAll = (event.metaKey || event.ctrlKey) && event.key === 'a';
    const isCutCommand = (event.metaKey || event.ctrlKey) && event.key === 'x';
    const isUndoRedo = (event.metaKey || event.ctrlKey) && (event.key === 'z' || event.key === 'y');
    const isCopyCommand = (event.metaKey || event.ctrlKey) && event.key === 'c';

    if (
      this.noteContentCount >= this.maxChars &&
      !allowedKeys.includes(event.key) &&
      !isCutCommand &&
      !isSelectAll &&
      !isUndoRedo &&
      !isCopyCommand
    ) {
      event.preventDefault();
    }
  }

  truncateHtmlPreservingFormat(htmlContent: string, maxLength: number): string {
    const safeHtml = this.sanitizer.sanitize(1, htmlContent) || '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = safeHtml;

    let currentLength = 0;
    const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT);

    const textNodes: Text[] = [];
    let textNode: Text | null;
    while ((textNode = walker.nextNode() as Text)) {
      textNodes.push(textNode);
    }

    for (let i = 0; i < textNodes.length; i++) {
      const node = textNodes[i];
      const nodeText = node.textContent || '';
      const newLength = currentLength + nodeText.length;
      if (newLength > maxLength) {
        const allowedLength = maxLength - currentLength;
        node.textContent = nodeText.substring(0, allowedLength);
        for (let j = i + 1; j < textNodes.length; j++) {
          textNodes[j].textContent = '';
        }
        break;
      }

      currentLength = newLength;
    }

    return tempDiv.innerHTML;
  }

  plainTextLength(): number {
    const strippedContent = this.stripHtml(this.note);
    return strippedContent.length;
  }

  stripHtml(htmlContent: string): string {
    const safeHtml = this.sanitizer.sanitize(1, htmlContent) || '';
    const tempElement = document.createElement('div');
    tempElement.innerHTML = safeHtml;
    return tempElement.textContent || tempElement.innerText || '';
  }

  // checkNote(e) {
  //   this.note = e.trim();
  //   this.cd.detectChanges();
  // }

  disableWhiteSpaceOnFirstCharacter(event) {
    if (event.target.selectionStart == 0 && (event.keyCode == 32 || event.keyCode == 9)) {
      event.preventDefault();
    }
  }

  handleInput(event) {
    this.noteTitle = event.target.value;
  }

  selectAllContent($event) {
    $event.target.select();
  }

  onToggleChange(event) {
    this.sharedVaultService.sendNoteCompletionMessage(event.checked);
  }

  closeDialog(dataCreated = false, title = null) {
    this.dialogRef.close({
      isSuccess: dataCreated,
      ...(title && { title: title }),
    });
  }

  ngOnDestroy(): void {
    this.editor.destroy();
    this.subscription.unsubscribe();
  }
}
